import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { CheckCircle2, Truck, MessageCircle, MessageSquareText } from "lucide-react";
import api, { inr } from "@/lib/api";

// Business contact — Vardhman Furniture, Bathinda
const BUSINESS_WHATSAPP = "919115800091"; // wa.me format (no + or spaces)
const BUSINESS_SMS = "+919115800091";
const BUSINESS_NAME = "Vardhman Furniture";

function buildConfirmationText(order) {
  if (!order) return "";
  const addr = order.shipping_address || {};
  const itemsList = (order.items || [])
    .map((i, idx) => `${idx + 1}. ${i.name} × ${i.quantity} — ₹${(i.price * i.quantity).toLocaleString("en-IN")}`)
    .join("\n");
  const method = order.payment_method === "cod" ? "Cash on Delivery" : "Paid Online (Razorpay)";
  const parts = [
    `Hi ${BUSINESS_NAME},`,
    `I've just placed an order on your website. Please confirm.`,
    ``,
    `Order ID: ${order.id}`,
    `Date: ${new Date(order.created_at || Date.now()).toLocaleString("en-IN")}`,
    `Payment: ${method}`,
    order.delivery_charge
      ? `Delivery charge: ₹${order.delivery_charge.toLocaleString("en-IN")}${order.delivery_distance_km ? ` (${order.delivery_distance_km} km)` : ""}`
      : null,
    `Total: ₹${(order.total || 0).toLocaleString("en-IN")}`,
    ``,
    `Items:`,
    itemsList,
    ``,
    `Delivery to:`,
    `${addr.name || ""} · ${addr.phone || ""}`,
    `${addr.line1 || ""}`,
    `${addr.city || ""}, ${addr.state || ""} - ${addr.pincode || ""}`,
  ].filter(Boolean);
  return parts.join("\n");
}

export default function OrderSuccessPage() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const method = params.get("method"); // "cod" | "online" | null
  const isCod = method === "cod";
  const Icon = isCod ? Truck : CheckCircle2;
  const [order, setOrder] = useState(null);

  useEffect(() => {
    if (!id) return;
    api.get(`/orders/${id}`).then((r) => setOrder(r.data)).catch(() => {});
  }, [id]);

  const confirmText = buildConfirmationText(order);
  const encoded = encodeURIComponent(confirmText);
  const waHref = `https://wa.me/${BUSINESS_WHATSAPP}?text=${encoded}`;
  // Use `?body=` (widely supported) — iOS also accepts `&body=` after `sms:number`
  const smsHref = `sms:${BUSINESS_SMS}?body=${encoded}`;

  return (
    <div className="max-w-3xl mx-auto px-6 md:px-12 py-24 text-center">
      <Icon size={56} className="mx-auto text-[#8B5A2B]" strokeWidth={1.2} />
      <p className="eyebrow mt-6">{isCod ? "Order Placed" : "Thank you"}</p>
      <h1 data-testid="order-success-title" className="font-serif text-4xl md:text-6xl mt-2 tracking-tight">
        {isCod ? "Order placed — cash on delivery." : "Your order is confirmed."}
      </h1>
      <p className="mt-4 text-stone-600">Order ID: <span className="font-mono text-sm">{id}</span></p>
      <p className="mt-2 text-stone-600 max-w-lg mx-auto">
        {isCod
          ? "Our team will call you shortly to confirm and schedule delivery. Please keep the exact amount ready at delivery."
          : "Our team will reach out shortly to confirm details and delivery timeline. Every piece is finished by hand — worth the wait."}
      </p>

      {order && (
        <div className="mt-10 border border-stone-200 bg-[#FAF6EF] p-6 md:p-8 text-left">
          <p className="eyebrow">Confirm with us instantly</p>
          <h2 className="font-serif text-2xl mt-1">Send us a confirmation on WhatsApp or SMS</h2>
          <p className="mt-2 text-sm text-stone-600">
            We recommend tapping the button below — it opens WhatsApp (or your SMS app) with your full order details pre-filled. Just tap send. This helps us confirm & prioritise your delivery.
          </p>
          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <a
              data-testid="confirm-whatsapp-btn"
              href={waHref}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 px-5 py-3 bg-[#25D366] text-white text-sm font-medium tracking-wide hover:opacity-90 transition-opacity"
            >
              <MessageCircle size={18} strokeWidth={1.8} /> Confirm on WhatsApp
            </a>
            <a
              data-testid="confirm-sms-btn"
              href={smsHref}
              className="inline-flex items-center justify-center gap-2 px-5 py-3 bg-[#1C1917] text-white text-sm font-medium tracking-wide hover:opacity-90 transition-opacity"
            >
              <MessageSquareText size={18} strokeWidth={1.8} /> Send SMS Confirmation
            </a>
          </div>

          <details className="mt-6">
            <summary className="text-xs uppercase tracking-widest text-stone-500 cursor-pointer hover:text-stone-800">Preview message</summary>
            <pre data-testid="confirm-message-preview" className="mt-3 whitespace-pre-wrap text-xs text-stone-700 bg-white border border-stone-200 p-4 font-sans leading-relaxed">{confirmText}</pre>
          </details>
        </div>
      )}

      <div className="mt-10 flex justify-center gap-4">
        <Link to="/account" className="btn-secondary">View My Orders</Link>
        <Link to="/" className="btn-primary">Back to Home</Link>
      </div>
    </div>
  );
}
